import type { CSSProperties } from "react";
import { useEffect, useMemo, useState } from "react";

import Skeleton from "../../components/states/Skeleton";
import { listCustomers, type CustomerRow } from "../../lib/admin/customers";
import {
  downloadAuditCsv,
  fetchAuditTimeline,
  filterAuditEntries,
  type AuditEntry,
} from "../../lib/admin/audit";

/**
 * MER-493: /admin/audit. Every admin write from the CRUD/refresh/
 * users/commercial children is findable here with actor + timestamp;
 * impersonation start/stop shows in the same timeline (the ticket's
 * explicit ask); CSV export.
 */

function utcStamp(iso: string): string {
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return iso;
  return `${t.toISOString().replace("T", " ").slice(0, 19)} UTC`;
}

interface PageState {
  entries: AuditEntry[];
  customers: CustomerRow[];
  isLoading: boolean;
  error: string | null;
}

export default function AdminAudit() {
  const [state, setState] = useState<PageState>({
    entries: [],
    customers: [],
    isLoading: true,
    error: null,
  });
  const [actorFilter, setActorFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [sinceFilter, setSinceFilter] = useState("");
  const [untilFilter, setUntilFilter] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [entries, customers] = await Promise.all([fetchAuditTimeline(500), listCustomers()]);
        if (cancelled) return;
        setState({ entries, customers, isLoading: false, error: null });
      } catch (err) {
        if (cancelled) return;
        setState((s) => ({
          ...s,
          isLoading: false,
          error: err instanceof Error ? err.message : String(err),
        }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const actionTypes = useMemo(
    () => Array.from(new Set(state.entries.map((e) => e.actionType))).sort(),
    [state.entries],
  );

  const filtered = useMemo(
    () =>
      filterAuditEntries(state.entries, {
        actor: actorFilter || undefined,
        actionType: actionFilter || undefined,
        customerSlug: customerFilter || undefined,
        since: sinceFilter || undefined,
        until: untilFilter || undefined,
      }),
    [state.entries, actorFilter, actionFilter, customerFilter, sinceFilter, untilFilter],
  );

  return (
    <main style={{ padding: "24px 32px", display: "grid", gap: 18 }}>
      <header style={headerStyle}>
        <div style={eyebrowStyle}>Admin</div>
        <h1 style={titleStyle}>Audit log</h1>
      </header>

      <section
        style={{
          ...panelStyle,
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr) auto",
          gap: 12,
        }}
      >
        <label style={filterLabelStyle}>
          Actor
          <input
            type="text"
            placeholder="email contains..."
            value={actorFilter}
            onChange={(e) => setActorFilter(e.target.value)}
            style={selectStyle}
          />
        </label>
        <label style={filterLabelStyle}>
          Action
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            style={selectStyle}
          >
            <option value="">Any action</option>
            {actionTypes.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>
        <label style={filterLabelStyle}>
          Customer
          <select
            value={customerFilter}
            onChange={(e) => setCustomerFilter(e.target.value)}
            style={selectStyle}
          >
            <option value="">All customers</option>
            {state.customers.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label style={filterLabelStyle}>
          Since
          <input
            type="date"
            value={sinceFilter}
            onChange={(e) => setSinceFilter(e.target.value)}
            style={selectStyle}
          />
        </label>
        <label style={filterLabelStyle}>
          Until
          <input
            type="date"
            value={untilFilter}
            onChange={(e) => setUntilFilter(e.target.value)}
            style={selectStyle}
          />
        </label>
        <button
          type="button"
          onClick={() => downloadAuditCsv(filtered)}
          disabled={filtered.length === 0}
          style={exportButtonStyle}
        >
          Export CSV
        </button>
      </section>

      {state.isLoading ? <Skeleton label="Loading audit log" height={280} /> : null}
      {state.error ? <ErrorCard message={state.error} /> : null}
      {!state.isLoading && !state.error ? (
        <section style={panelStyle}>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            {filtered.length} {filtered.length === 1 ? "entry" : "entries"}
          </div>
          {filtered.length === 0 ? (
            <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-muted)" }}>
              No admin actions recorded yet.
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={headerCellStyle}>When</th>
                  <th style={headerCellStyle}>Actor</th>
                  <th style={headerCellStyle}>Action</th>
                  <th style={headerCellStyle}>Customer</th>
                  <th style={headerCellStyle}>Source</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={`${e.source}-${e.id}`}>
                    <td style={cellStyle}>{utcStamp(e.occurredAt)}</td>
                    <td style={cellStyle}>{e.actor ?? "(unknown)"}</td>
                    <td style={{ ...cellStyle, textTransform: "capitalize" }}>
                      {e.actionType.replace(/_/g, " ")}
                    </td>
                    <td style={cellStyle}>{e.customerSlug ?? "-"}</td>
                    <td style={{ ...cellStyle, color: "var(--text-muted)" }}>
                      {e.source === "impersonation" ? "Impersonation" : "Admin action"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      ) : null}
    </main>
  );
}

function ErrorCard(props: { message: string }) {
  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: 12,
        padding: 20,
        color: "var(--flag-red)",
        fontSize: 13,
      }}
    >
      {props.message}
    </div>
  );
}

const headerStyle: CSSProperties = {
  padding: "22px 28px",
  background: "linear-gradient(110deg, var(--brand-mint) 0%, #E6F3EE 50%, #FFFFFF 100%)",
  border: "1px solid var(--brand-line)",
  borderRadius: 8,
};

const eyebrowStyle: CSSProperties = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 1,
  color: "var(--text-2)",
  fontWeight: 700,
};

const titleStyle: CSSProperties = {
  margin: "6px 0 0 0",
  fontSize: 26,
  fontWeight: 800,
  letterSpacing: -0.5,
  color: "var(--text)",
};

const panelStyle: CSSProperties = {
  background: "var(--bg-card)",
  border: "1px solid var(--border-subtle)",
  borderRadius: 12,
  padding: 18,
  display: "grid",
  gap: 12,
};

const filterLabelStyle: CSSProperties = {
  display: "grid",
  gap: 4,
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--text-muted)",
  fontWeight: 700,
};

const selectStyle: CSSProperties = {
  font: "inherit",
  fontSize: 13,
  padding: "6px 8px",
  borderRadius: 6,
  border: "1px solid var(--border-subtle)",
  background: "var(--bg-card)",
  color: "var(--text)",
};

const exportButtonStyle: CSSProperties = {
  alignSelf: "end",
  font: "inherit",
  fontSize: 13,
  fontWeight: 700,
  padding: "8px 14px",
  borderRadius: 6,
  border: "1px solid var(--brand-line)",
  background: "var(--brand-deep)",
  color: "#FFFFFF",
  cursor: "pointer",
};

const cellStyle: CSSProperties = {
  padding: "10px 14px",
  fontSize: 13,
  borderBottom: "1px solid var(--border-subtle)",
  verticalAlign: "top",
  color: "var(--text)",
};

const headerCellStyle: CSSProperties = {
  textAlign: "left",
  padding: "8px 14px",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: "var(--text-muted)",
  borderBottom: "1px solid var(--border-subtle)",
  fontWeight: 700,
};
